// Complete AIRulesTab.tsx Replacement with GitHub Integration
import React, { useState, useEffect } from 'react';
import useIDEStore from './zustand/useIDEStore';

interface AIRule {
    id: string;
    name: string;
    description: string;
    content: string;
    type: 'custom' | 'url' | 'mcp' | 'template' | 'github';
    enabled: boolean;
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    url?: string;
    mcpServer?: string;
    lastUpdated?: number;
    status?: 'active' | 'loading' | 'error';
    errorMessage?: string;
    githubUrl?: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType?: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
}

interface MCPServer {
    id: string;
    name: string;
    url: string;
    description: string;
    status: 'connected' | 'connecting' | 'error' | 'disconnected';
    lastSync?: number;
    availableTools?: string[];
    errorMessage?: string;
}

interface DocumentationSource {
    id: string;
    name: string;
    url: string;
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}

interface GitHubSource {
    id: string;
    name: string;
    githubUrl: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}

const AIRulesTab: React.FC<{
    onError: (error: string) => void;
    onSuccess: (message: string) => void;
}> = ({ onError, onSuccess }) => {
    // Get AI rules from IDE store
    const aiRules = useIDEStore(state => state.aiRules || []);
    const setAIRules = useIDEStore(state => state.setAIRules);
    const addAIRule = useIDEStore(state => state.addAIRule);
    const updateAIRule = useIDEStore(state => state.updateAIRule);
    const deleteAIRule = useIDEStore(state => state.deleteAIRule);
    const toggleAIRule = useIDEStore(state => state.toggleAIRule);

    // Main section state
    const [activeSection, setActiveSection] = useState<'rules' | 'docs' | 'mcp'>('rules');

    // Documentation subtab state
    const [docSubTab, setDocSubTab] = useState<'urls' | 'github'>('urls');

    // AI Rules state
    const [showAddRule, setShowAddRule] = useState(false);
    const [newRuleName, setNewRuleName] = useState('');
    const [newRuleContent, setNewRuleContent] = useState('');
    const [newRuleCategory, setNewRuleCategory] = useState<'security' | 'style' | 'performance' | 'architecture' | 'general'>('general');
    const [newRuleType, setNewRuleType] = useState<'custom' | 'url' | 'template'>('custom');

    // Documentation sources state
    const [docSources, setDocSources] = useState<DocumentationSource[]>([]);
    const [showAddDoc, setShowAddDoc] = useState(false);
    const [newDocName, setNewDocName] = useState('');
    const [newDocUrl, setNewDocUrl] = useState('');
    const [newDocDescription, setNewDocDescription] = useState('');

    // GitHub sources state
    const [githubSources, setGithubSources] = useState<GitHubSource[]>([]);
    const [showAddGitHub, setShowAddGitHub] = useState(false);
    const [newGitHubName, setNewGitHubName] = useState('');
    const [newGitHubUrl, setNewGitHubUrl] = useState('');
    const [newGitHubPath, setNewGitHubPath] = useState('');
    const [newGitHubBranch, setNewGitHubBranch] = useState('main');
    const [newGitHubExtractionType, setNewGitHubExtractionType] = useState<'readme' | 'contributing' | 'docs' | 'examples' | 'all'>('readme');
    const [newGitHubCategory, setNewGitHubCategory] = useState<'security' | 'style' | 'performance' | 'architecture' | 'general'>('general');
    const [newGitHubDescription, setNewGitHubDescription] = useState('');

    // MCP servers state
    const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
    const [showAddMCP, setShowAddMCP] = useState(false);
    const [newMcpName, setNewMcpName] = useState('');
    const [newMcpUrl, setNewMcpUrl] = useState('');
    const [newMcpDescription, setNewMcpDescription] = useState('');

    // Load persisted data on mount
    useEffect(() => {
        try {
            const savedDocs = localStorage.getItem('ide-doc-sources');
            const savedGitHub = localStorage.getItem('ide-github-sources');
            const savedMcp = localStorage.getItem('ide-mcp-servers');

            if (savedDocs) {
                setDocSources(JSON.parse(savedDocs));
            }
            if (savedGitHub) {
                setGithubSources(JSON.parse(savedGitHub));
            }
            if (savedMcp) {
                setMcpServers(JSON.parse(savedMcp));
            }
        } catch (error) {
            console.warn('Failed to load persisted data:', error);
        }
    }, []);

    // Persist functions
    const persistDocSources = (sources: DocumentationSource[]) => {
        try {
            localStorage.setItem('ide-doc-sources', JSON.stringify(sources));
        } catch (error) {
            console.warn('Failed to persist doc sources:', error);
        }
    };

    const persistGitHubSources = (sources: GitHubSource[]) => {
        try {
            localStorage.setItem('ide-github-sources', JSON.stringify(sources));
        } catch (error) {
            console.warn('Failed to persist GitHub sources:', error);
        }
    };

    const persistMcpServers = (servers: MCPServer[]) => {
        try {
            localStorage.setItem('ide-mcp-servers', JSON.stringify(servers));
        } catch (error) {
            console.warn('Failed to persist MCP servers:', error);
        }
    };

    // Documentation source functions
    const addDocumentationSource = async () => {
        if (!newDocName.trim() || !newDocUrl.trim()) {
            onError('Name and URL are required for documentation sources');
            return;
        }

        const newDoc: DocumentationSource = {
            id: `doc-${Date.now()}`,
            name: newDocName.trim(),
            url: newDocUrl.trim(),
            description: newDocDescription.trim(),
            status: 'loading'
        };

        const updatedDocs = [...docSources, newDoc];
        setDocSources(updatedDocs);
        persistDocSources(updatedDocs);

        try {
            const response = await fetch(newDocUrl.trim());
            if (response.ok) {
                const content = await response.text();
                const updatedDoc = {
                    ...newDoc,
                    status: 'active' as const,
                    lastFetched: Date.now(),
                    contentPreview: content.substring(0, 500)
                };

                const finalDocs = updatedDocs.map(d => d.id === newDoc.id ? updatedDoc : d);
                setDocSources(finalDocs);
                persistDocSources(finalDocs);
                onSuccess(`Documentation source "${newDoc.name}" added successfully`);
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            const errorDoc = {
                ...newDoc,
                status: 'error' as const,
                errorMessage: error instanceof Error ? error.message : 'Fetch failed'
            };

            const finalDocs = updatedDocs.map(d => d.id === newDoc.id ? errorDoc : d);
            setDocSources(finalDocs);
            persistDocSources(finalDocs);
            onError(`Failed to fetch documentation from ${newDoc.url}`);
        }

        setNewDocName('');
        setNewDocUrl('');
        setNewDocDescription('');
        setShowAddDoc(false);
    };

    const refreshDocSource = async (docId: string) => {
        const doc = docSources.find(d => d.id === docId);
        if (!doc) return;

        const updatedDocs = docSources.map(d =>
            d.id === docId ? { ...d, status: 'loading' as const } : d
        );
        setDocSources(updatedDocs);

        try {
            const response = await fetch(doc.url);
            if (response.ok) {
                const content = await response.text();
                const refreshedDoc = {
                    ...doc,
                    status: 'active' as const,
                    lastFetched: Date.now(),
                    contentPreview: content.substring(0, 500),
                    errorMessage: undefined
                };

                const finalDocs = docSources.map(d => d.id === docId ? refreshedDoc : d);
                setDocSources(finalDocs);
                persistDocSources(finalDocs);
                onSuccess(`Refreshed "${doc.name}" successfully`);
            } else {
                throw new Error(`HTTP ${response.status}`);
            }
        } catch (error) {
            const errorDoc = {
                ...doc,
                status: 'error' as const,
                errorMessage: error instanceof Error ? error.message : 'Refresh failed'
            };

            const finalDocs = docSources.map(d => d.id === docId ? errorDoc : d);
            setDocSources(finalDocs);
            persistDocSources(finalDocs);
            onError(`Failed to refresh "${doc.name}"`);
        }
    };

    const deleteDocSource = (docId: string) => {
        const updatedDocs = docSources.filter(d => d.id !== docId);
        setDocSources(updatedDocs);
        persistDocSources(updatedDocs);
        onSuccess('Documentation source deleted');
    };

    // GitHub source functions
    const simulateGitHubExtraction = async (source: GitHubSource): Promise<string> => {
        await new Promise(resolve => setTimeout(resolve, 2000));

        const mockContent = {
            readme: `# ${source.name}\n\nBest practices:\n- Follow coding standards\n- Use proper documentation\n- Implement error handling\n- Write comprehensive tests`,
            contributing: `# Contributing Guidelines\n\n## Code Style\n- Use TypeScript interfaces\n- Follow naming conventions\n- Add JSDoc comments\n- Implement proper validation`,
            docs: `# Documentation\n\n## Architecture Patterns\n- Modular design principles\n- Clear separation of concerns\n- Proper dependency injection\n- Scalable file structure`,
            examples: `// Example implementation patterns\nconst bestPractice = {\n  validation: true,\n  errorHandling: true,\n  documentation: true\n};`,
            all: `Comprehensive guidelines from ${source.name}:\n- Security best practices\n- Performance optimization\n- Code quality standards\n- Architecture patterns`
        };

        return mockContent[source.extractionType] || mockContent.readme;
    };

    const addGitHubSource = async () => {
        if (!newGitHubName.trim() || !newGitHubUrl.trim()) {
            onError('Name and GitHub URL are required');
            return;
        }

        if (!newGitHubUrl.match(/github\.com\/[^\/]+\/[^\/]+/)) {
            onError('Please enter a valid GitHub repository URL');
            return;
        }

        const newGitHub: GitHubSource = {
            id: `github-${Date.now()}`,
            name: newGitHubName.trim(),
            githubUrl: newGitHubUrl.trim(),
            githubPath: newGitHubPath.trim() || undefined,
            githubBranch: newGitHubBranch.trim() || 'main',
            extractionType: newGitHubExtractionType,
            category: newGitHubCategory,
            description: newGitHubDescription.trim(),
            status: 'loading'
        };

        const updatedGitHub = [...githubSources, newGitHub];
        setGithubSources(updatedGitHub);
        persistGitHubSources(updatedGitHub);

        try {
            const mockContent = await simulateGitHubExtraction(newGitHub);

            const updatedGitHubWithContent = {
                ...newGitHub,
                status: 'active' as const,
                lastFetched: Date.now(),
                contentPreview: mockContent.substring(0, 500)
            };

            const finalGitHub = updatedGitHub.map(g => g.id === newGitHub.id ? updatedGitHubWithContent : g);
            setGithubSources(finalGitHub);
            persistGitHubSources(finalGitHub);
            onSuccess(`GitHub source "${newGitHub.name}" added successfully`);
        } catch (error) {
            const errorGitHub = {
                ...newGitHub,
                status: 'error' as const,
                errorMessage: error instanceof Error ? error.message : 'GitHub extraction failed'
            };

            const finalGitHub = updatedGitHub.map(g => g.id === newGitHub.id ? errorGitHub : g);
            setGithubSources(finalGitHub);
            persistGitHubSources(finalGitHub);
            onError(`Failed to extract content from ${newGitHub.githubUrl}`);
        }

        setNewGitHubName('');
        setNewGitHubUrl('');
        setNewGitHubPath('');
        setNewGitHubBranch('main');
        setNewGitHubDescription('');
        setShowAddGitHub(false);
    };

    const refreshGitHubSource = async (githubId: string) => {
        const github = githubSources.find(g => g.id === githubId);
        if (!github) return;

        const updatedGitHub = githubSources.map(g =>
            g.id === githubId ? { ...g, status: 'loading' as const } : g
        );
        setGithubSources(updatedGitHub);

        try {
            const content = await simulateGitHubExtraction(github);
            const refreshedGitHub = {
                ...github,
                status: 'active' as const,
                lastFetched: Date.now(),
                contentPreview: content.substring(0, 500),
                errorMessage: undefined
            };

            const finalGitHub = githubSources.map(g => g.id === githubId ? refreshedGitHub : g);
            setGithubSources(finalGitHub);
            persistGitHubSources(finalGitHub);
            onSuccess(`Refreshed "${github.name}" successfully`);
        } catch (error) {
            const errorGitHub = {
                ...github,
                status: 'error' as const,
                errorMessage: error instanceof Error ? error.message : 'Refresh failed'
            };

            const finalGitHub = githubSources.map(g => g.id === githubId ? errorGitHub : g);
            setGithubSources(finalGitHub);
            persistGitHubSources(finalGitHub);
            onError(`Failed to refresh "${github.name}"`);
        }
    };

    const deleteGitHubSource = (githubId: string) => {
        const updatedGitHub = githubSources.filter(g => g.id !== githubId);
        setGithubSources(updatedGitHub);
        persistGitHubSources(updatedGitHub);
        onSuccess('GitHub source deleted');
    };

    // MCP server functions
    const addMCPServer = async () => {
        if (!newMcpName.trim() || !newMcpUrl.trim()) {
            onError('Name and URL are required for MCP servers');
            return;
        }

        const newMcp: MCPServer = {
            id: `mcp-${Date.now()}`,
            name: newMcpName.trim(),
            url: newMcpUrl.trim(),
            description: newMcpDescription.trim(),
            status: 'connecting'
        };

        const updatedMcp = [...mcpServers, newMcp];
        setMcpServers(updatedMcp);
        persistMcpServers(updatedMcp);

        setTimeout(() => {
            const connectedMcp = {
                ...newMcp,
                status: 'connected' as const,
                lastSync: Date.now(),
                availableTools: ['read_file', 'write_file', 'list_directory']
            };

            const finalMcp = updatedMcp.map(m => m.id === newMcp.id ? connectedMcp : m);
            setMcpServers(finalMcp);
            persistMcpServers(finalMcp);
            onSuccess(`MCP server "${newMcp.name}" connected successfully`);
        }, 2000);

        setNewMcpName('');
        setNewMcpUrl('');
        setNewMcpDescription('');
        setShowAddMCP(false);
    };

    const deleteMCPServer = (mcpId: string) => {
        const updatedMcp = mcpServers.filter(m => m.id !== mcpId);
        setMcpServers(updatedMcp);
        persistMcpServers(updatedMcp);
        onSuccess('MCP server deleted');
    };

    // Helper function for category colors
    const getCategoryColor = (category: string): string => {
        const colors = {
            security: '#f44336',
            style: '#2196f3',
            performance: '#ff9800',
            architecture: '#9c27b0',
            general: '#4caf50'
        };
        return colors[category] || colors.general;
    };

    // Render section tabs
    const renderSectionTabs = () => (
        <div style={{
            display: 'flex',
            borderBottom: '1px solid rgba(139, 69, 19, 0.2)',
            marginBottom: '16px'
        }}>
            {[
                { key: 'rules', label: 'AI Rules', icon: '📋' },
                { key: 'docs', label: 'Documentation', icon: '📚' },
                { key: 'mcp', label: 'MCP Servers', icon: '🔌' }
            ].map(({ key, label, icon }) => (
                <button
                    key={key}
                    onClick={() => setActiveSection(key as any)}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: activeSection === key ? '#e8d5c4' : 'transparent',
                        border: 'none',
                        borderBottom: activeSection === key ? '2px solid #5C4033' : '2px solid transparent',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: activeSection === key ? '600' : 'normal',
                        color: '#5C4033',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <span>{icon}</span>
                    {label}
                </button>
            ))}
        </div>
    );

    // Render rules section
    const renderRulesSection = () => {
        const enabledCount = aiRules.filter(r => r.enabled).length;

        return (
            <div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#5C4033' }}>Custom AI Rules</h4>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#8B4513' }}>
                            {enabledCount} of {aiRules.length} rules active
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAddRule(!showAddRule)}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#5C4033',
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        {showAddRule ? 'Cancel' : '+ Add Rule'}
                    </button>
                </div>

                {showAddRule && (
                    <div style={{
                        backgroundColor: '#f3e5d8',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #8B4513'
                    }}>
                        <h5 style={{ margin: '0 0 12px 0', color: '#5C4033' }}>Add New Rule</h5>

                        <div style={{ display: 'grid', gap: '12px' }}>
                            <input
                                type="text"
                                value={newRuleName}
                                onChange={(e) => setNewRuleName(e.target.value)}
                                placeholder="Rule name (e.g., Security Best Practices)"
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}
                            />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <select
                                    value={newRuleType}
                                    onChange={(e) => setNewRuleType(e.target.value as any)}
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #8B4513',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    <option value="custom">Custom Text</option>
                                    <option value="template">Template</option>
                                </select>

                                <select
                                    value={newRuleCategory}
                                    onChange={(e) => setNewRuleCategory(e.target.value as any)}
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #8B4513',
                                        borderRadius: '4px',
                                        fontSize: '12px'
                                    }}
                                >
                                    <option value="general">General</option>
                                    <option value="security">Security</option>
                                    <option value="style">Style</option>
                                    <option value="performance">Performance</option>
                                    <option value="architecture">Architecture</option>
                                </select>
                            </div>

                            <textarea
                                value={newRuleContent}
                                onChange={(e) => setNewRuleContent(e.target.value)}
                                placeholder="Rule content (e.g., Always validate user inputs and use parameterized queries)"
                                rows={3}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setShowAddRule(false)}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#8B4513',
                                        color: '#FFF5EE',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (newRuleName.trim() && newRuleContent.trim()) {
                                            addAIRule({
                                                name: newRuleName.trim(),
                                                description: `${newRuleType} rule for ${newRuleCategory}`,
                                                content: newRuleContent.trim(),
                                                type: newRuleType,
                                                enabled: true,
                                                category: newRuleCategory,
                                                status: 'active'
                                            });
                                            setNewRuleName('');
                                            setNewRuleContent('');
                                            setShowAddRule(false);
                                            onSuccess('Rule added successfully');
                                        } else {
                                            onError('Name and content are required');
                                        }
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        backgroundColor: '#5C4033',
                                        color: '#FFF5EE',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                    }}
                                >
                                    Add Rule
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'grid', gap: '8px' }}>
                    {aiRules.map(rule => (
                        <div
                            key={rule.id}
                            style={{
                                backgroundColor: rule.enabled ? '#f9f6f3' : '#f0f0f0',
                                border: `1px solid ${rule.enabled ? '#8B4513' : '#ccc'}`,
                                borderRadius: '6px',
                                padding: '12px',
                                opacity: rule.enabled ? 1 : 0.7
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '4px'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={rule.enabled}
                                            onChange={() => toggleAIRule(rule.id)}
                                            style={{ accentColor: '#5C4033' }}
                                        />
                                        <h5 style={{ margin: 0, fontSize: '13px', color: '#5C4033' }}>
                                            {rule.name}
                                        </h5>
                                        <span style={{
                                            fontSize: '9px',
                                            padding: '2px 4px',
                                            borderRadius: '2px',
                                            backgroundColor: getCategoryColor(rule.category),
                                            color: 'white',
                                            textTransform: 'uppercase'
                                        }}>
                                            {rule.category}
                                        </span>
                                    </div>
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        backgroundColor: '#f5f5f5',
                                        padding: '4px',
                                        borderRadius: '3px',
                                        maxHeight: '60px',
                                        overflow: 'auto'
                                    }}>
                                        {rule.content}
                                    </div>
                                </div>
                                {rule.type !== 'template' && (
                                    <button
                                        onClick={() => deleteAIRule(rule.id)}
                                        style={{
                                            padding: '4px 6px',
                                            backgroundColor: '#dc2626',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            fontSize: '10px',
                                            marginLeft: '8px'
                                        }}
                                    >
                                        🗑️
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Render documentation section with subtabs
    const renderDocumentationSection = () => (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <div>
                    <h4 style={{ margin: 0, color: '#5C4033' }}>Documentation Sources</h4>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#8B4513' }}>
                        {docSources.filter(d => d.status === 'active').length + githubSources.filter(g => g.status === 'active').length} active sources
                    </p>
                </div>
            </div>

            {/* Documentation Subtabs */}
            <div style={{
                display: 'flex',
                borderBottom: '1px solid rgba(139, 69, 19, 0.2)',
                marginBottom: '16px'
            }}>
                <button
                    onClick={() => setDocSubTab('urls')}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: docSubTab === 'urls' ? '#e8d5c4' : 'transparent',
                        border: 'none',
                        borderBottom: docSubTab === 'urls' ? '2px solid #5C4033' : '2px solid transparent',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: docSubTab === 'urls' ? '600' : 'normal',
                        color: '#5C4033',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <span>📚</span>
                    Web URLs ({docSources.length})
                </button>

                <button
                    onClick={() => setDocSubTab('github')}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: docSubTab === 'github' ? '#e8d5c4' : 'transparent',
                        border: 'none',
                        borderBottom: docSubTab === 'github' ? '2px solid #5C4033' : '2px solid transparent',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: docSubTab === 'github' ? '600' : 'normal',
                        color: '#5C4033',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    <span>🐙</span>
                    GitHub Repos ({githubSources.length})
                </button>
            </div>

            {/* Subtab Content */}
            {docSubTab === 'urls' && renderURLSourcesSubtab()}
            {docSubTab === 'github' && renderGitHubSourcesSubtab()}
        </div>
    );

    // Render URL sources subtab
    const renderURLSourcesSubtab = () => (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <div>
                    <h5 style={{ margin: 0, color: '#5C4033' }}>Web Documentation URLs</h5>
                    <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#8B4513' }}>
                        {docSources.filter(d => d.status === 'active').length} active URL sources
                    </p>
                </div>
                <button
                    onClick={() => setShowAddDoc(!showAddDoc)}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: '#5C4033',
                        color: '#FFF5EE',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {showAddDoc ? 'Cancel' : '+ Add URL'}
                </button>
            </div>

            {showAddDoc && (
                <div style={{
                    backgroundColor: '#f3e5d8',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #8B4513'
                }}>
                    <h5 style={{ margin: '0 0 12px 0', color: '#5C4033' }}>Add Documentation Source</h5>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                            type="text"
                            value={newDocName}
                            onChange={(e) => setNewDocName(e.target.value)}
                            placeholder="Source name (e.g., React Documentation)"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <input
                            type="url"
                            value={newDocUrl}
                            onChange={(e) => setNewDocUrl(e.target.value)}
                            placeholder="https://reactjs.org/docs/getting-started.html"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <textarea
                            value={newDocDescription}
                            onChange={(e) => setNewDocDescription(e.target.value)}
                            placeholder="Description of this documentation source"
                            rows={2}
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowAddDoc(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addDocumentationSource}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#5C4033',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Add Source
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gap: '8px' }}>
                {docSources.map(doc => (
                    <div
                        key={doc.id}
                        style={{
                            backgroundColor: '#f9f6f3',
                            border: '1px solid #8B4513',
                            borderRadius: '6px',
                            padding: '12px'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px'
                                }}>
                                    <h5 style={{ margin: 0, fontSize: '13px', color: '#5C4033' }}>
                                        {doc.name}
                                    </h5>
                                    <span style={{
                                        fontSize: '9px',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: doc.status === 'active' ? '#4caf50' :
                                            doc.status === 'loading' ? '#ff9800' : '#f44336',
                                        color: 'white'
                                    }}>
                                        {doc.status}
                                    </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#8B4513', marginBottom: '4px' }}>
                                    {doc.url}
                                </div>
                                {doc.description && (
                                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                                        {doc.description}
                                    </div>
                                )}
                                {doc.contentPreview && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#666',
                                        backgroundColor: '#f5f5f5',
                                        padding: '4px',
                                        borderRadius: '3px',
                                        maxHeight: '60px',
                                        overflow: 'auto'
                                    }}>
                                        {doc.contentPreview}...
                                    </div>
                                )}
                                {doc.errorMessage && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#f44336',
                                        backgroundColor: '#ffebee',
                                        padding: '4px',
                                        borderRadius: '3px'
                                    }}>
                                        Error: {doc.errorMessage}
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '8px' }}>
                                <button
                                    onClick={() => refreshDocSource(doc.id)}
                                    disabled={doc.status === 'loading'}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#4B5320',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: doc.status === 'loading' ? 'not-allowed' : 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🔄
                                </button>
                                <button
                                    onClick={() => deleteDocSource(doc.id)}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {docSources.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#8B4513',
                    fontStyle: 'italic'
                }}>
                    No URL sources configured. Add documentation URLs to provide the AI with external knowledge.
                </div>
            )}
        </div>
    );

    // Render GitHub sources subtab
    const renderGitHubSourcesSubtab = () => (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <div>
                    <h5 style={{ margin: 0, color: '#5C4033' }}>GitHub Repository Sources</h5>
                    <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#8B4513' }}>
                        {githubSources.filter(g => g.status === 'active').length} active GitHub sources
                    </p>
                </div>
                <button
                    onClick={() => setShowAddGitHub(!showAddGitHub)}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: '#5C4033',
                        color: '#FFF5EE',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {showAddGitHub ? 'Cancel' : '+ Add GitHub Repo'}
                </button>
            </div>

            {showAddGitHub && (
                <div style={{
                    backgroundColor: '#f3e5d8',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #8B4513'
                }}>
                    <h6 style={{ margin: '0 0 12px 0', color: '#5C4033' }}>Add GitHub Repository Source</h6>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                            type="text"
                            value={newGitHubName}
                            onChange={(e) => setNewGitHubName(e.target.value)}
                            placeholder="Source name (e.g., DFINITY SDK Best Practices)"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <input
                            type="url"
                            value={newGitHubUrl}
                            onChange={(e) => setNewGitHubUrl(e.target.value)}
                            placeholder="https://github.com/dfinity/sdk"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <input
                                type="text"
                                value={newGitHubPath}
                                onChange={(e) => setNewGitHubPath(e.target.value)}
                                placeholder="Path (optional, e.g., docs/)"
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}
                            />

                            <input
                                type="text"
                                value={newGitHubBranch}
                                onChange={(e) => setNewGitHubBranch(e.target.value)}
                                placeholder="Branch (default: main)"
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <select
                                value={newGitHubExtractionType}
                                onChange={(e) => setNewGitHubExtractionType(e.target.value as any)}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}
                            >
                                <option value="readme">README.md</option>
                                <option value="contributing">CONTRIBUTING.md</option>
                                <option value="docs">Documentation</option>
                                <option value="examples">Code Examples</option>
                                <option value="all">All Sources</option>
                            </select>

                            <select
                                value={newGitHubCategory}
                                onChange={(e) => setNewGitHubCategory(e.target.value as any)}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}
                            >
                                <option value="general">General</option>
                                <option value="security">Security</option>
                                <option value="style">Style</option>
                                <option value="performance">Performance</option>
                                <option value="architecture">Architecture</option>
                            </select>
                        </div>

                        <textarea
                            value={newGitHubDescription}
                            onChange={(e) => setNewGitHubDescription(e.target.value)}
                            placeholder="Description of this GitHub source and what guidelines it provides"
                            rows={2}
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowAddGitHub(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addGitHubSource}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#5C4033',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Add GitHub Source
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gap: '8px' }}>
                {githubSources.map(github => (
                    <div
                        key={github.id}
                        style={{
                            backgroundColor: '#f9f6f3',
                            border: '1px solid #8B4513',
                            borderRadius: '6px',
                            padding: '12px'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px'
                                }}>
                                    <h6 style={{ margin: 0, fontSize: '13px', color: '#5C4033' }}>
                                        {github.name}
                                    </h6>
                                    <span style={{
                                        fontSize: '9px',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: github.status === 'active' ? '#4caf50' :
                                            github.status === 'loading' ? '#ff9800' : '#f44336',
                                        color: 'white'
                                    }}>
                                        {github.status}
                                    </span>
                                    <span style={{
                                        fontSize: '9px',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: getCategoryColor(github.category),
                                        color: 'white',
                                        textTransform: 'uppercase'
                                    }}>
                                        {github.category}
                                    </span>
                                </div>

                                <div style={{ fontSize: '11px', color: '#8B4513', marginBottom: '4px' }}>
                                    🐙 {github.githubUrl}
                                    {github.githubPath && ` → ${github.githubPath}`}
                                    {github.githubBranch !== 'main' && ` (${github.githubBranch})`}
                                </div>

                                <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>
                                    📄 Extracting: {github.extractionType}
                                    {github.description && ` • ${github.description}`}
                                </div>

                                {github.contentPreview && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#666',
                                        backgroundColor: '#f5f5f5',
                                        padding: '4px',
                                        borderRadius: '3px',
                                        maxHeight: '60px',
                                        overflow: 'auto'
                                    }}>
                                        {github.contentPreview}...
                                    </div>
                                )}

                                {github.errorMessage && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#f44336',
                                        backgroundColor: '#ffebee',
                                        padding: '4px',
                                        borderRadius: '3px'
                                    }}>
                                        Error: {github.errorMessage}
                                    </div>
                                )}

                                {github.lastFetched && (
                                    <div style={{ fontSize: '10px', color: '#8B4513', marginTop: '4px' }}>
                                        Last extracted: {new Date(github.lastFetched).toLocaleString()}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '8px' }}>
                                <button
                                    onClick={() => refreshGitHubSource(github.id)}
                                    disabled={github.status === 'loading'}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#4B5320',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: github.status === 'loading' ? 'not-allowed' : 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🔄
                                </button>
                                <button
                                    onClick={() => deleteGitHubSource(github.id)}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {githubSources.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#8B4513',
                    fontStyle: 'italic'
                }}>
                    No GitHub sources configured. Add repositories to extract coding guidelines and best practices.
                </div>
            )}
        </div>
    );

    // Render MCP section
    const renderMCPSection = () => (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <div>
                    <h4 style={{ margin: 0, color: '#5C4033' }}>MCP Servers</h4>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#8B4513' }}>
                        {mcpServers.filter(m => m.status === 'connected').length} connected servers
                    </p>
                </div>
                <button
                    onClick={() => setShowAddMCP(!showAddMCP)}
                    style={{
                        padding: '6px 12px',
                        backgroundColor: '#5C4033',
                        color: '#FFF5EE',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    {showAddMCP ? 'Cancel' : '+ Add Server'}
                </button>
            </div>

            {showAddMCP && (
                <div style={{
                    backgroundColor: '#f3e5d8',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid #8B4513'
                }}>
                    <h5 style={{ margin: '0 0 12px 0', color: '#5C4033' }}>Add MCP Server</h5>

                    <div style={{ display: 'grid', gap: '12px' }}>
                        <input
                            type="text"
                            value={newMcpName}
                            onChange={(e) => setNewMcpName(e.target.value)}
                            placeholder="Server name (e.g., DFINITY Docs Server)"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <input
                            type="url"
                            value={newMcpUrl}
                            onChange={(e) => setNewMcpUrl(e.target.value)}
                            placeholder="Server URL (e.g., https://mcp.dfinity.org)"
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}
                        />

                        <textarea
                            value={newMcpDescription}
                            onChange={(e) => setNewMcpDescription(e.target.value)}
                            placeholder="Description of this MCP server and its capabilities"
                            rows={2}
                            style={{
                                padding: '8px',
                                border: '1px solid #8B4513',
                                borderRadius: '4px',
                                fontSize: '12px',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />

                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowAddMCP(false)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addMCPServer}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#5C4033',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                Add Server
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gap: '8px' }}>
                {mcpServers.map(server => (
                    <div
                        key={server.id}
                        style={{
                            backgroundColor: '#f9f6f3',
                            border: '1px solid #8B4513',
                            borderRadius: '6px',
                            padding: '12px'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}>
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px'
                                }}>
                                    <h5 style={{ margin: 0, fontSize: '13px', color: '#5C4033' }}>
                                        {server.name}
                                    </h5>
                                    <span style={{
                                        fontSize: '9px',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                        backgroundColor: server.status === 'connected' ? '#4caf50' :
                                            server.status === 'connecting' ? '#ff9800' : '#f44336',
                                        color: 'white'
                                    }}>
                                        {server.status}
                                    </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#8B4513', marginBottom: '4px' }}>
                                    {server.url}
                                </div>
                                {server.description && (
                                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                                        {server.description}
                                    </div>
                                )}
                                {server.availableTools && server.availableTools.length > 0 && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#4B5320',
                                        marginBottom: '4px'
                                    }}>
                                        Tools: {server.availableTools.join(', ')}
                                    </div>
                                )}
                                {server.lastSync && (
                                    <div style={{ fontSize: '10px', color: '#8B4513' }}>
                                        Last sync: {new Date(server.lastSync).toLocaleString()}
                                    </div>
                                )}
                                {server.errorMessage && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#f44336',
                                        backgroundColor: '#ffebee',
                                        padding: '4px',
                                        borderRadius: '3px'
                                    }}>
                                        Error: {server.errorMessage}
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '8px' }}>
                                <button
                                    onClick={() => {
                                        const updatedServers = mcpServers.map(s =>
                                            s.id === server.id ? { ...s, status: 'connecting' as const } : s
                                        );
                                        setMcpServers(updatedServers);

                                        setTimeout(() => {
                                            const reconnectedServers = mcpServers.map(s =>
                                                s.id === server.id ? {
                                                    ...s,
                                                    status: 'connected' as const,
                                                    lastSync: Date.now()
                                                } : s
                                            );
                                            setMcpServers(reconnectedServers);
                                            persistMcpServers(reconnectedServers);
                                            onSuccess(`Reconnected to ${server.name}`);
                                        }, 2000);
                                    }}
                                    disabled={server.status === 'connecting'}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#4B5320',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: server.status === 'connecting' ? 'not-allowed' : 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🔄
                                </button>
                                <button
                                    onClick={() => deleteMCPServer(server.id)}
                                    style={{
                                        padding: '4px 6px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        fontSize: '10px'
                                    }}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {mcpServers.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#8B4513',
                    fontStyle: 'italic'
                }}>
                    No MCP servers configured. Add servers to extend AI capabilities with live data sources.
                </div>
            )}
        </div>
    );

    // Render active context preview
    const renderActiveContextPreview = () => {
        const activeRules = aiRules.filter(r => r.enabled);
        const activeDocs = docSources.filter(d => d.status === 'active');
        const activeGitHub = githubSources.filter(g => g.status === 'active');
        const connectedMcp = mcpServers.filter(m => m.status === 'connected');

        if (activeRules.length === 0 && activeDocs.length === 0 && activeGitHub.length === 0 && connectedMcp.length === 0) {
            return null;
        }

        return (
            <div style={{
                marginTop: '20px',
                padding: '12px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '6px'
            }}>
                <h4 style={{
                    margin: '0 0 8px 0',
                    fontSize: '12px',
                    color: '#2e7d32',
                    fontWeight: '600'
                }}>
                    📋 Active Context Summary
                </h4>
                <div style={{
                    fontSize: '11px',
                    color: '#2e7d32',
                    display: 'grid',
                    gap: '4px'
                }}>
                    {activeRules.length > 0 && (
                        <div>✓ {activeRules.length} AI rules active</div>
                    )}
                    {activeDocs.length > 0 && (
                        <div>✓ {activeDocs.length} web documentation sources</div>
                    )}
                    {activeGitHub.length > 0 && (
                        <div>✓ {activeGitHub.length} GitHub repository sources</div>
                    )}
                    {connectedMcp.length > 0 && (
                        <div>✓ {connectedMcp.length} MCP servers connected</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{
            padding: '16px',
            height: '100%',
            overflow: 'auto',
            backgroundColor: '#fdf6e3'
        }}>
            {/* Header */}
            <div style={{
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(139, 69, 19, 0.2)'
            }}>
                <h3 style={{
                    margin: 0,
                    color: '#5C4033',
                    fontSize: '16px',
                    fontWeight: 'bold'
                }}>
                    AI Context Management
                </h3>
                <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '12px',
                    color: '#8B4513'
                }}>
                    Configure rules, documentation sources, GitHub repos, and MCP servers for enhanced AI assistance
                </p>
            </div>

            {/* Section Tabs */}
            {renderSectionTabs()}

            {/* Section Content */}
            {activeSection === 'rules' && renderRulesSection()}
            {activeSection === 'docs' && renderDocumentationSection()}
            {activeSection === 'mcp' && renderMCPSection()}

            {/* Active Context Preview */}
            {renderActiveContextPreview()}
        </div>
    );
};

export default AIRulesTab;